import { Box, Typography, useTheme } from '@mui/material';
import { LuDownload } from 'react-icons/lu';
import Button from 'components/Common/Button';
import TranscriptToolRow, { JourneyItem } from './TranscriptToolRow';
import { getVisibleJourneyItems } from './transcriptUtils';
import TranscriptHandoffRow from './TranscriptHandoffRow';

export default function TranscriptSection({
  journey,
  formatTranscriptTimestamp,
  onDownload
}: {
  journey?: JourneyItem[];
  formatTranscriptTimestamp: (timestamp?: number) => string;
  onDownload: () => void;
}) {
  const theme = useTheme();

  return (
    <Box sx={{ bgcolor: '#F8F9FA', p: 2, borderRadius: 2, minHeight: '100%' }}>
      <Box display='flex' justifyContent='space-between' alignItems='center' mt={2}>
        <Typography color='primary' fontWeight={600}>
          Transcript
        </Typography>

        <Button color='secondary' variant='text' onClick={onDownload}>
          <LuDownload />
          <Typography ml={1}>Download Transcript</Typography>
        </Button>
      </Box>

      <Box mt={1}>
        {getVisibleJourneyItems(journey).map((item: JourneyItem, index) => {
          const key = item.id ?? index;
          const speaker = item.extra?.speaker === 'live_operator' ? 'operator' : item.role;
          const timestamp = formatTranscriptTimestamp(item.created_at);

          if (
            item.type === 'agent_handoff' &&
            !['alpha_agent', 'payment_collection_agent'].includes(item.new_agent_id!)
          ) {
            return <TranscriptHandoffRow key={`handoff-${index}`} agentName={item.new_agent_id!} />;
          }

          if (item.type === 'function_call' && item.arguments) {
            return (
              <TranscriptToolRow
                key={key}
                variant='invocation'
                title={`Tool Invocation: ${item.name}`}
                callId={item.call_id}
                content={item.arguments}
                timestamp={timestamp}
              />
            );
          }

          if (item.type === 'function_call_output') {
            return (
              <TranscriptToolRow
                key={key}
                variant='result'
                title='Tool Result'
                callId={item.call_id}
                content={item.output ?? ''}
                timestamp={timestamp}
              />
            );
          }

          if (['user', 'assistant', 'operator'].includes(speaker ?? '')) {
            return (
              <Box key={key} display='flex' my={1} justifyContent={speaker == 'user' ? 'end' : 'start'}>
                <Box
                  border='1px solid #ECECEC'
                  bgcolor={theme.palette.background.paper}
                  borderRadius='10px'
                  p='6px 10px'
                  maxWidth='75%'
                >
                  <Typography display='inline' fontWeight={600}>
                    {speaker === 'assistant' ? 'Agent: ' : speaker === 'operator' ? 'Operator: ' : 'Human: '}
                  </Typography>
                  <Typography display='inline'>{item?.message || item?.content}</Typography>
                  <Typography fontSize='11px' color='text.secondary' mt={0.5}>
                    {timestamp}
                  </Typography>
                </Box>
              </Box>
            );
          }

          return null;
        })}
      </Box>
    </Box>
  );
}
